import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, Send, CheckCircle, XCircle, FileText, AlertCircle, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useApi } from '../hooks/useApi';

// Quarter-hour slot labels for a Warsaw delivery date, DST-aware (EU rule: clocks
// change on the last Sunday of March / October). Normal day: 96 slots; spring-forward
// day: 92 (no 02:xx); fall-back day: 100 (02:xx twice, A = CEST, B = CET).
const fixingIIQuarterSlots = (isoDate) => {
  const [year, month, day] = (isoDate || '').split('-').map(Number);
  const lastSunday = (m) => {
    const last = new Date(Date.UTC(year, m, 0)); // last day of month m (1-based)
    return last.getUTCDate() - last.getUTCDay();
  };
  const springForward = month === 3 && day === lastSunday(3);
  const fallBack = month === 10 && day === lastSunday(10);
  const count = springForward ? 92 : fallBack ? 100 : 96;
  const pad = (n) => String(n).padStart(2, '0');

  return Array.from({ length: count }, (_, quarter) => {
    let hour = Math.floor(quarter / 4);
    const minute = (quarter % 4) * 15;
    let suffix = '';
    if (springForward && hour >= 2) hour += 1;
    if (fallBack) {
      if (hour === 2) suffix = 'A';
      else if (hour === 3) { hour = 2; suffix = 'B'; }
      else if (hour > 3) hour -= 1;
    }
    const endHour = minute === 45 ? (hour + 1) % 24 : hour;
    const endMinute = (minute + 15) % 60;
    return `${pad(hour)}:${pad(minute)}-${pad(endHour)}:${pad(endMinute)}${suffix}`;
  });
};

const DataUpload = () => {
  const { fetchGet, fetchPost, fetchPostJson } = useApi();
  // Check if we're in debug mode (you can set this via environment variable or config)
  const DEBUG_MODE =
    process.env.NODE_ENV !== 'production' ||            // dev server
    process.env.REACT_APP_DEBUG === 'true' ||           // build-time flag
    window.location.hostname === 'localhost';           // fallback
  
  // Get tomorrow's date in Warsaw timezone (CET/CEST) for Nord Pool
  const today = new Date();
  const warsawTime = new Date(today.toLocaleString("en-US", {timeZone: "Europe/Warsaw"}));
  const warsawTomorrow = new Date(today.toLocaleString("en-US", {timeZone: "Europe/Warsaw"}));
  warsawTime.setDate(warsawTime.getDate() + 1); // Add 1 day for tomorrow
  warsawTomorrow.setDate(warsawTomorrow.getDate() + 1); // Tomorrow for max date
  const todayFormatted = `${String(warsawTime.getDate()).padStart(2, '0')}/${String(warsawTime.getMonth() + 1).padStart(2, '0')}/${warsawTime.getFullYear()}`;
  const todayISO = `${warsawTime.getFullYear()}-${String(warsawTime.getMonth() + 1).padStart(2, '0')}-${String(warsawTime.getDate()).padStart(2, '0')}`;

  // Get max date (today + 1) for Nord Pool and Fixing I Prices
  const maxDateISO = `${warsawTomorrow.getFullYear()}-${String(warsawTomorrow.getMonth() + 1).padStart(2, '0')}-${String(warsawTomorrow.getDate()).padStart(2, '0')}`;

  // Get tomorrow's date for TGE default (current day + 1) and max date restriction
  const warsawTomorrowForTGE = new Date(today.toLocaleString("en-US", {timeZone: "Europe/Warsaw"}));
  warsawTomorrowForTGE.setDate(warsawTomorrowForTGE.getDate() + 1); // Add 1 day for tomorrow
  const tomorrowTGEISO = `${warsawTomorrowForTGE.getFullYear()}-${String(warsawTomorrowForTGE.getMonth() + 1).padStart(2, '0')}-${String(warsawTomorrowForTGE.getDate()).padStart(2, '0')}`;
  const tomorrowTGEFormatted = `${String(warsawTomorrowForTGE.getDate()).padStart(2, '0')}/${String(warsawTomorrowForTGE.getMonth() + 1).padStart(2, '0')}/${warsawTomorrowForTGE.getFullYear()}`;

  // Keep today's date for TGE max date restriction (allowing up to tomorrow)
  const maxDateTGE = maxDateISO; // Now allowing tomorrow as max date
  
  const [activeTab, setActiveTab] = useState('nordpool');
  const [formData, setFormData] = useState({
    date: todayISO,
    dateFormatted: todayFormatted,
    tgeDate: tomorrowTGEISO,
    tgeDateFormatted: tomorrowTGEFormatted,
    polandActualDate: todayISO,
    polandActualDateFormatted: todayFormatted,
    fixingIIDate: todayISO,
    fixingIIDateFormatted: todayFormatted,
    fixingIIOrdersDate: todayISO,
    fixingIIOrdersDateFormatted: todayFormatted,
    curvesDate: todayISO,
    curvesDateFormatted: todayFormatted,
    exchangeRate: '',
    fixingIIPricesText: '', // Single text area for all 24 Fixing II prices
    files: {
      nordpool: {
        flows: null,
        constraints: null
      },
      tge: {
        transactions: null
      },
      fixingI: {
        file15min: null,
        file60min: null
      },
      fixingii: {
        report: null
      },
      fixingIIOrders: {
        orders: null
      },
      curves: {
        fixingICurves: null,
        fixingIICurves: null
      }
    }
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Base price status for tomorrow (current date + 1)
  const [basePriceStatus, setBasePriceStatus] = useState(null); // null=loading, {exists, price}

  useEffect(() => {
    const checkBasePrice = async () => {
      try {
        const response = await fetchGet(`/base-price-status/${todayISO}`);
        const data = await response.json();
        if (data.status === 'success') {
          setBasePriceStatus({ exists: data.exists, price: data.price || null });
        } else {
          setBasePriceStatus({ exists: false, price: null });
        }
      } catch {
        setBasePriceStatus({ exists: false, price: null });
      }
    };
    checkBasePrice();
  }, [fetchGet, todayISO]);

  // Validate all required fields
  const validateForm = () => {
    const errors = [];
    
    // Check date
    if (!formData.date || formData.dateFormatted.length !== 10) {
      errors.push('Valid date in DD/MM/YYYY format');
    }
    
    // Check exchange rate
    if (!formData.exchangeRate || parseFloat(formData.exchangeRate) <= 0) {
      errors.push('EUR/PLN exchange rate (must be greater than 0)');
    }
    
    if (!DEBUG_MODE) {
      // Check Nord Pool files only in production
      const nordpoolFiles = Object.entries(formData.files.nordpool);
      const missingNordpool = nordpoolFiles.filter(([key, file]) => !file && key !== 'constraints');
      if (missingNordpool.length > 0) {
        const fileNames = missingNordpool.map(([key]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
        errors.push(`Nord Pool files: ${fileNames.join(', ')}`);
      }
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Check if form is complete
  const isFormComplete = () => {
    const hasDate = formData.date && formData.dateFormatted.length === 10;
    const hasExchangeRate = formData.exchangeRate && parseFloat(formData.exchangeRate) > 0;
    
    if (DEBUG_MODE) {
      // In debug mode, only date and exchange rate are required
      return hasDate && hasExchangeRate;
    }
    
    const allNordpoolFiles = formData.files.nordpool.flows !== null;
    
    return hasDate && hasExchangeRate && allNordpoolFiles;
  };

  const handleFileChange = (system, fileType, file) => {
    setFormData(prev => ({
      ...prev,
      files: {
        ...prev.files,
        [system]: {
          ...prev.files[system],
          [fileType]: file
        }
      }
    }));
  };

  const handleSubmit = async () => {
    setValidationErrors([]);
    
    if (!validateForm()) {
      setStatus({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    // Validate Nord Pool file names (in both DEBUG and production modes)
    // Use noon (12:00) to avoid timezone issues around DST transitions
    const selectedDate = new Date(formData.date + 'T12:00:00');
    const dayBefore = new Date(selectedDate);
    dayBefore.setDate(dayBefore.getDate() - 1);

    // Format dates as YYYY-MM-DD
    const selectedDateStr = formData.date;
    const dayBeforeStr = dayBefore.toISOString().split('T')[0];
    
    // Function to proceed with submission after all validations
    const proceedWithSubmission = async () => {
      // Check constraints file (should be selected date)
      if (formData.files.nordpool.constraints) {
        const constraintsFilename = formData.files.nordpool.constraints.name;
        const constraintsPattern = new RegExp(`auction_capacities_DayAhead_PL_${selectedDateStr}_MWh\\.xlsx$`);
        if (!constraintsPattern.test(constraintsFilename)) {
          setStatus({ 
            type: 'error', 
            message: `Wrong Constraints file! Expected filename to contain: auction_capacities_DayAhead_PL_${selectedDateStr}_MWh.xlsx` 
          });
          return;
        }
      }

      setIsSubmitting(true);
      setStatus({ type: 'info', message: 'Uploading files...' });

      try {
        const formDataToSend = new FormData();
        formDataToSend.append('date', formData.date);
        formDataToSend.append('exchange_rate', formData.exchangeRate);

        // Add Nord Pool files
        Object.entries(formData.files.nordpool).forEach(([key, file]) => {
          if (file) {
            formDataToSend.append(`nordpool_${key}`, file);
          }
        });

        const response = await fetchPost('/upload', formDataToSend);

        if (response.ok) {
          await response.json();
          setStatus({ type: 'success', message: 'All files uploaded successfully!' });
          
          // Reset form
          setFormData({
            date: todayISO,
            dateFormatted: todayFormatted,
            tgeDate: tomorrowTGEISO,
            tgeDateFormatted: tomorrowTGEFormatted,
            polandActualDate: todayISO,
            polandActualDateFormatted: todayFormatted,
            fixingIIDate: todayISO,
            fixingIIDateFormatted: todayFormatted,
            fixingIIOrdersDate: todayISO,
            fixingIIOrdersDateFormatted: todayFormatted,
            curvesDate: todayISO,
            curvesDateFormatted: todayFormatted,
            exchangeRate: '',
            fixingIIPricesText: '',
            files: {
              nordpool: {
                flows: null,
                constraints: null
              },
              tge: {
                transactions: null
              },
              fixingI: {
                file15min: null,
                file60min: null
              },
              fixingii: {
                report: null
              },
              fixingIIOrders: {
                orders: null
              },
              curves: {
                fixingICurves: null,
                fixingIICurves: null
              }
            }
          });
          setValidationErrors([]);
        } else {
          setStatus({ type: 'error', message: `Upload failed: ${await response.text()}` });
        }
      } catch (error) {
        setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      } finally {
        setIsSubmitting(false);
      }
    };
    
    // Check flows file (should be day before)
    if (formData.files.nordpool.flows) {
      const flowsFilename = formData.files.nordpool.flows.name;
      const flowsPattern = new RegExp(`auction_flows_DayAhead_PL_${dayBeforeStr}_MWh\\.xlsx$`);
      if (!flowsPattern.test(flowsFilename)) {
        setStatus({ 
          type: 'error', 
          message: `Wrong Flows file! Expected filename to contain: auction_flows_DayAhead_PL_${dayBeforeStr}_MWh.xlsx` 
        });
        return;
      }

      // Check if cell C5 contains "PL - GER (MW)"
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            
            console.log('Sheet names:', workbook.SheetNames);
            console.log('First sheet:', firstSheet);
            
            // Get value from cell C5
            const cellC5 = firstSheet['C5'];
            console.log('Cell C5:', cellC5);
            
            const cellValue = cellC5 ? (cellC5.v || cellC5.w || '') : '';
            console.log('Cell C5 value:', cellValue);
            
            if (cellValue !== 'PL - GER (MWh)') {
              setStatus({ 
                type: 'error', 
                message: `Invalid format of Flows file: Cell C5 must contain "PL - GER (MWh)", but found "${cellValue}"` 
              });
              return;
            }

            // If validation passes, continue with submission
            proceedWithSubmission();
          } catch (error) {
            console.error('Error reading Excel file:', error);
            setStatus({ 
              type: 'error', 
              message: 'Error reading Flows file format: ' + error.message 
            });
            return;
          }
        };
        
        reader.readAsArrayBuffer(formData.files.nordpool.flows);
        return; // Exit here, proceedWithSubmission will be called after file is read
      } catch (error) {
        setStatus({ 
          type: 'error', 
          message: 'Error validating Flows file' 
        });
        return;
      }
    } else {
      // If no flows file, proceed directly
      proceedWithSubmission();
    }
  };

  const handleTGESubmit = async () => {
    if (!formData.files.tge.transactions || !formData.tgeDate) {
      setStatus({ type: 'error', message: 'Please select a file and date' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Uploading TGE transactions...' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('date', formData.tgeDate);
      formDataToSend.append('file', formData.files.tge.transactions);

      const response = await fetchPost('/upload-tge-transactions', formDataToSend);

      const result = await response.json();
      
      if (result.status === 'success') {
        setStatus({ type: 'success', message: 'TGE transactions uploaded successfully!' });
        
        // Reset TGE form fields
        setFormData(prev => ({
          ...prev,
          files: {
            ...prev.files,
            tge: {
              transactions: null
            }
          }
        }));
      } else {
        setStatus({ type: 'error', message: result.message || 'Upload failed' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      console.error('Error uploading TGE transactions:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFixingIFilesSubmit = async () => {
    setStatus({ type: '', message: '' });

    const file15 = formData.files.fixingI.file15min;
    const file60 = formData.files.fixingI.file60min;

    if (!formData.polandActualDate) {
      setStatus({ type: 'error', message: 'Please select a date' });
      return;
    }
    if (!file15 || !file60) {
      setStatus({ type: 'error', message: 'Please select both the Fixing I 15 min and 60 min files' });
      return;
    }

    // Filename validation: 15 min must start with wynik_publiczny_{YYYYMMDD}_LOCAL_15,
    // 60 min must start with wynik_publiczny_{YYYYMMDD}_LOCAL_60 (date = selected day)
    const dateCompact = formData.polandActualDate.replace(/-/g, '');
    const expectedPrefix15 = `wynik_publiczny_${dateCompact}_LOCAL_15`;
    const expectedPrefix60 = `wynik_publiczny_${dateCompact}_LOCAL_60`;
    if (!file15.name.startsWith(expectedPrefix15)) {
      setStatus({ type: 'error', message: `Wrong Fixing I 15 min file! Filename must start with: ${expectedPrefix15}` });
      return;
    }
    if (!file60.name.startsWith(expectedPrefix60)) {
      setStatus({ type: 'error', message: `Wrong Fixing I 60 min file! Filename must start with: ${expectedPrefix60}` });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Uploading Fixing I files...' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('date', formData.polandActualDate);
      formDataToSend.append('file_15min', file15);
      formDataToSend.append('file_60min', file60);

      const response = await fetchPost('/upload-fixing-i-files', formDataToSend);
      const result = await response.json();

      if (result.status === 'success') {
        setStatus({
          type: 'success',
          message: `Fixing I files uploaded successfully! ${result.rows_15min} quarterly and ${result.rows_60min} hourly rows saved.`
        });
        setFormData(prev => ({
          ...prev,
          files: {
            ...prev.files,
            fixingI: { file15min: null, file60min: null }
          }
        }));
      } else {
        setStatus({ type: 'error', message: result.message || 'Upload failed' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      console.error('Error uploading Fixing I files:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fixingIISlots = fixingIIQuarterSlots(formData.fixingIIDate);

  const handleFixingIISubmit = async () => {
    // Clear any previous status
    setStatus({ type: '', message: '' });

    if (!formData.fixingIIPricesText.trim()) {
      setStatus({ type: 'error', message: 'Please enter price data' });
      return;
    }

    // Step 1: Replace commas with periods
    let processedText = formData.fixingIIPricesText.replace(/,/g, '.');

    // Step 2: Trim whitespaces (keep only newlines)
    const lines = processedText.split('\n');
    const processedLines = lines.map(line => line.trim()).filter(line => line !== '');

    // Step 3: Validate that we have one value per quarter (96, or 92/100 on DST days)
    const expectedCount = fixingIISlots.length;
    if (processedLines.length !== expectedCount) {
      setStatus({
        type: 'error',
        message: `Expected ${expectedCount} quarterly values (every 15 minutes${expectedCount !== 96 ? ', DST day' : ''}), but got ${processedLines.length}. Please provide exactly ${expectedCount} price values.`
      });
      return;
    }

    // Step 4: Validate each value is a valid number
    const validatedValues = [];
    const invalidLines = [];

    processedLines.forEach((line, index) => {
      // Use a regex to ensure the string is a valid number (int or float)
      // This regex matches: optional minus, digits, optional decimal point and more digits
      const isValidNumber = /^-?\d+(\.\d+)?$/.test(line);
      const value = parseFloat(line);

      if (!isValidNumber || isNaN(value)) {
        invalidLines.push({
          quarter: index,
          timeSlot: fixingIISlots[index],
          value: line
        });
      } else {
        validatedValues.push(value);
      }
    });

    // Check if any validation failed
    if (invalidLines.length > 0) {
      const errorDetails = invalidLines.map(item =>
        `Quarter ${item.quarter} (${item.timeSlot}): "${item.value}"`
      ).join(', ');

      setStatus({
        type: 'error',
        message: `Invalid values found at: ${errorDetails}. Please enter numeric values only.`
      });
      return;
    }

    // All validation passed - send to backend
    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Uploading Fixing II prices...' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('date', formData.fixingIIDate);
      formDataToSend.append('prices', validatedValues.join(','));

      const response = await fetchPost('/upload-fixing-ii-prices', formDataToSend);

      const result = await response.json();

      if (result.status === 'success') {
        setStatus({
          type: 'success',
          message: `Fixing II prices uploaded successfully! Average price: ${result.average_price.toFixed(2)} PLN/MWh`
        });

        // Clear the form
        setFormData(prev => ({
          ...prev,
          fixingIIPricesText: ''
        }));
      } else {
        setStatus({ type: 'error', message: result.message || 'Upload failed' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      console.error('Error uploading Fixing II prices:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFixingIIOrdersSubmit = async () => {
    setStatus({ type: '', message: '' });

    if (!formData.files.fixingIIOrders.orders || !formData.fixingIIOrdersDate) {
      setStatus({ type: 'error', message: 'Please select a file and date' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Uploading Fixing II Orders...' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('date', formData.fixingIIOrdersDate);
      formDataToSend.append('file', formData.files.fixingIIOrders.orders);

      const response = await fetchPost('/upload-fixing-ii-orders', formDataToSend);
      const result = await response.json();

      if (result.status === 'success') {
        setStatus({ type: 'success', message: 'Fixing II Orders uploaded successfully!' });

        // Reset form fields
        setFormData(prev => ({
          ...prev,
          files: {
            ...prev.files,
            fixingIIOrders: {
              orders: null
            }
          }
        }));
      } else {
        setStatus({ type: 'error', message: result.message || 'Upload failed' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      console.error('Error uploading Fixing II Orders:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCurvesSubmit = async () => {
    setStatus({ type: '', message: '' });

    if (!formData.files.curves.fixingICurves && !formData.files.curves.fixingIICurves) {
      setStatus({ type: 'error', message: 'Please select at least one file to upload' });
      return;
    }

    if (!formData.curvesDate) {
      setStatus({ type: 'error', message: 'Please select a date' });
      return;
    }

    // Validate file types
    if (formData.files.curves.fixingICurves) {
      const fileName = formData.files.curves.fixingICurves.name.toLowerCase();
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xml')) {
        setStatus({ type: 'error', message: 'Fixing I Curves must be an .xlsx (hourly) or .xml (15-min) file' });
        return;
      }
    }

    if (formData.files.curves.fixingIICurves) {
      const fileName = formData.files.curves.fixingIICurves.name.toLowerCase();
      if (!fileName.endsWith('.xml')) {
        setStatus({ type: 'error', message: 'Fixing II Curves must be an .xml file' });
        return;
      }
    }

    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Uploading Curves...' });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('date', formData.curvesDate);

      if (formData.files.curves.fixingICurves) {
        formDataToSend.append('fixing_i_curves', formData.files.curves.fixingICurves);
      }
      if (formData.files.curves.fixingIICurves) {
        formDataToSend.append('fixing_ii_curves', formData.files.curves.fixingIICurves);
      }

      const response = await fetchPost('/upload-curves', formDataToSend);
      const result = await response.json();

      if (result.status === 'success') {
        const hasFixingI = formData.files.curves.fixingICurves;
        const hasFixingII = formData.files.curves.fixingIICurves;

        // Calculate market influence for uploaded curves
        if (hasFixingI || hasFixingII) {
          const calculations = [];

          if (hasFixingI) {
            calculations.push({ name: 'Fixing I', endpoint: '/calculate-market-influence' });
          }
          if (hasFixingII) {
            calculations.push({ name: 'Fixing II', endpoint: '/calculate-fixing-ii-market-influence' });
          }

          setStatus({ type: 'info', message: `Curves uploaded. Calculating market influence for ${calculations.map(c => c.name).join(' and ')}...` });

          const results = [];
          for (const calc of calculations) {
            try {
              const response = await fetchPostJson(calc.endpoint, { date: formData.curvesDate });
              const calcResult = await response.json();
              results.push({ name: calc.name, success: calcResult.status === 'success', message: calcResult.message });
            } catch (error) {
              console.error(`Error calculating ${calc.name} market influence:`, error);
              results.push({ name: calc.name, success: false, message: error.message });
            }
          }

          const successCount = results.filter(r => r.success).length;
          const failedCalcs = results.filter(r => !r.success);

          if (successCount === calculations.length) {
            setStatus({ type: 'success', message: `Curves uploaded and market influence calculated for ${calculations.map(c => c.name).join(' and ')}!` });
          } else if (successCount > 0) {
            const successNames = results.filter(r => r.success).map(r => r.name).join(', ');
            const failedNames = failedCalcs.map(r => r.name).join(', ');
            setStatus({ type: 'warning', message: `Curves uploaded. ${successNames} calculated successfully. ${failedNames} failed: ${failedCalcs[0]?.message}` });
          } else {
            setStatus({ type: 'warning', message: `Curves uploaded, but market influence calculation failed: ${failedCalcs[0]?.message}` });
          }
        } else {
          setStatus({ type: 'success', message: 'Curves uploaded successfully!' });
        }

        // Reset form fields
        setFormData(prev => ({
          ...prev,
          files: {
            ...prev.files,
            curves: {
              fixingICurves: null,
              fixingIICurves: null
            }
          }
        }));
      } else {
        setStatus({ type: 'error', message: result.message || 'Upload failed' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      console.error('Error uploading Curves:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const FileInput = ({ label, system, fileType, icon: Icon = FileText, required = !DEBUG_MODE, accept = null }) => (
    <div className={`bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-all ${!formData.files[system][fileType] && required ? 'border-2 border-orange-200' : ''}`}>
      <label className="block cursor-pointer">
        <div className="flex items-center space-x-3 mb-2">
          <Icon className="w-5 h-5 text-gray-600" />
          <span className="font-medium text-gray-700">{label}</span>
          {required && <span className="text-red-500">*</span>}
        </div>
        <input
          type="file"
          required={required}
          accept={accept}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all"
          onChange={(e) => handleFileChange(system, fileType, e.target.files[0])}
        />
        {formData.files[system][fileType] ? (
          <p className="mt-2 text-xs text-green-600 flex items-center">
            <CheckCircle className="w-3 h-3 mr-1" />
            {formData.files[system][fileType].name}
          </p>
        ) : (
          <p className="mt-2 text-xs text-orange-600">{required ? 'Required' : 'Optional'}</p>
        )}
      </label>
    </div>
  );

  const fileStats = {
    nordpool: formData.files?.nordpool ? Object.values(formData.files.nordpool).filter(f => f).length : 0,
    nordpoolTotal: formData.files?.nordpool ? Object.keys(formData.files.nordpool).length : 2,
    tge: formData.files?.tge ? Object.values(formData.files.tge).filter(f => f).length : 0,
    tgeTotal: formData.files?.tge ? Object.keys(formData.files.tge).length : 1
  };

  return (
    <div className="space-y-8">
      {/* Tab Menu */}
      <div className="bg-gray-100 p-1 rounded-lg flex space-x-1">
        <button
          onClick={() => setActiveTab('nordpool')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
            activeTab === 'nordpool'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Nord Pool
        </button>
        <button
          onClick={() => setActiveTab('tge')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
            activeTab === 'tge'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          TGE
        </button>
        <button
          onClick={() => setActiveTab('polandactual')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
            activeTab === 'polandactual'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Fixing I Prices
        </button>
        <button
          onClick={() => setActiveTab('fixingii')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
            activeTab === 'fixingii'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Fixing II Prices
        </button>
      </div>

      {/* Second Row of Tabs */}
      <div className="bg-gray-100 p-1 rounded-lg flex space-x-1">
        <button
          onClick={() => setActiveTab('fixingIIOrders')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
            activeTab === 'fixingIIOrders'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Fixing II Orders
        </button>
        <button
          onClick={() => setActiveTab('curves')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
            activeTab === 'curves'
              ? 'bg-white text-gray-800 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Curves
        </button>
      </div>

      {/* Nord Pool Tab Content */}
      {activeTab === 'nordpool' && (
        <>
          {/* Date Selection */}
          <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
            <label className="flex items-center space-x-3 text-lg font-semibold text-gray-700 mb-3">
              <Calendar className="w-6 h-6" />
              <span>Data to predict for</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.date}
              max={maxDateISO}
              onChange={(e) => {
                const newDate = e.target.value;
                if (newDate) {
                  const [year, month, day] = newDate.split('-');
                  const formatted = `${day}/${month}/${year}`;
                  setFormData(prev => ({ 
                    ...prev, 
                    date: newDate,
                    dateFormatted: formatted
                  }));
                }
              }}
              className={`w-full px-4 py-3 border-2 ${formData.date ? 'border-green-400' : 'border-gray-300'} rounded-lg focus:border-gray-500 focus:outline-none transition-all font-mono text-lg`}
            />
          </div>

          {/* Nord Pool Section */}
          <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-all">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="w-1 h-8 bg-gray-600 mr-3"></div>
              Nord Pool
              <span className="ml-auto text-sm font-normal text-gray-600">
                {fileStats.nordpool}/{fileStats.nordpoolTotal} files uploaded
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileInput label="Flows" system="nordpool" fileType="flows" />
              <div>
                <FileInput label="Constraints (optional)" system="nordpool" fileType="constraints" required={false} />
                {!formData.files.nordpool.constraints && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center px-4">
                    <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />
                    ENTSO-E data will be used automatically
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Exchange Rate */}
          <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
            <label className="flex items-center space-x-3 text-lg font-semibold text-gray-700 mb-3">
              <DollarSign className="w-6 h-6" />
              <span>EUR/PLN Exchange Rate</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.0001"
              placeholder="e.g., 4.3215"
              required
              value={formData.exchangeRate}
              onChange={(e) => setFormData({ ...formData, exchangeRate: e.target.value })}
              className={`w-full px-4 py-3 border-2 ${formData.exchangeRate && parseFloat(formData.exchangeRate) > 0 ? 'border-green-400' : 'border-gray-300'} rounded-lg focus:border-gray-500 focus:outline-none transition-all`}
            />
            {(!formData.exchangeRate || parseFloat(formData.exchangeRate) <= 0) && (
              <p className="mt-2 text-sm text-gray-600">Required - must be greater than 0</p>
            )}
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="w-full">
                  <p className="font-semibold text-red-800 mb-2">Missing required fields:</p>
                  <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !isFormComplete()}
            className={`w-full bg-gray-800 text-white py-4 px-6 rounded-xl font-semibold text-lg transform transition-all flex items-center justify-center space-x-3 ${
              isFormComplete() 
                ? 'hover:bg-gray-900 hover:scale-[1.02]' 
                : 'opacity-50 cursor-not-allowed'
            }`}
          >
            <Send className="w-6 h-6" />
            <span>{isSubmitting ? 'Uploading...' : 'Submit Nord Pool Files'}</span>
          </button>
        </>
      )}

      {/* TGE Tab Content */}
      {activeTab === 'tge' && (
        <>
          {/* Date Selection */}
          <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
            <label className="flex items-center space-x-3 text-lg font-semibold text-gray-700 mb-3">
              <Calendar className="w-6 h-6" />
              <span>Date of TGE transactions</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.tgeDate}
              max={maxDateTGE}
              onChange={(e) => {
                const newDate = e.target.value;
                if (newDate) {
                  const [year, month, day] = newDate.split('-');
                  const formatted = `${day}/${month}/${year}`;
                  setFormData(prev => ({ 
                    ...prev, 
                    tgeDate: newDate,
                    tgeDateFormatted: formatted
                  }));
                }
              }}
              className={`w-full px-4 py-3 border-2 ${formData.tgeDate ? 'border-green-400' : 'border-gray-300'} rounded-lg focus:border-gray-500 focus:outline-none transition-all font-mono text-lg`}
            />
          </div>

          {/* Base Price Status */}
          <div className={`flex items-center space-x-3 px-4 py-3 rounded-lg border ${
            basePriceStatus === null
              ? 'bg-gray-50 border-gray-200'
              : basePriceStatus.exists
                ? 'bg-green-50 border-green-300'
                : 'bg-red-50 border-red-300'
          }`}>
            {basePriceStatus === null ? (
              <>
                <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                <span className="text-sm text-gray-500">Checking base price for {todayISO}...</span>
              </>
            ) : basePriceStatus.exists ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700">
                  Base price for <span className="font-mono font-semibold">{todayISO}</span>: <span className="font-bold">{basePriceStatus.price} PLN</span>
                </span>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-red-500" />
                <span className="text-sm text-red-600">
                  No base price in DB for <span className="font-mono font-semibold">{todayISO}</span>
                </span>
              </>
            )}
          </div>

          {/* TGE Section */}
          <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-all">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="w-1 h-8 bg-gray-600 mr-3"></div>
              TGE
              <span className="ml-auto text-sm font-normal text-gray-600">
                {fileStats.tge}/{fileStats.tgeTotal} files uploaded
              </span>
            </h2>

            <div className="grid grid-cols-1 gap-4">
              <FileInput label="Transactions" system="tge" fileType="transactions" />
            </div>
          </div>

          {/* Submit Button for TGE */}
          <button
            onClick={handleTGESubmit}
            disabled={isSubmitting || !formData.files.tge.transactions || !formData.tgeDate}
            className={`w-full bg-gray-800 text-white py-4 px-6 rounded-xl font-semibold text-lg transform transition-all flex items-center justify-center space-x-3 ${
              formData.files.tge.transactions && formData.tgeDate
                ? 'hover:bg-gray-900 hover:scale-[1.02]' 
                : 'opacity-50 cursor-not-allowed'
            }`}
          >
            <Send className="w-6 h-6" />
            <span>{isSubmitting ? 'Uploading...' : 'Submit TGE Data'}</span>
          </button>
        </>
      )}

      {/* Fixing I Prices Tab Content */}
      {activeTab === 'polandactual' && (
        <>
          {/* Date Selection */}
          <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
            <label className="flex items-center space-x-3 text-lg font-semibold text-gray-700 mb-3">
              <Calendar className="w-6 h-6" />
              <span>Fixing I prices for:</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.polandActualDate}
              max={maxDateISO}
              onChange={(e) => {
                const newDate = e.target.value;
                if (newDate) {
                  const [year, month, day] = newDate.split('-');
                  const formatted = `${day}/${month}/${year}`;
                  setFormData(prev => ({ 
                    ...prev, 
                    polandActualDate: newDate,
                    polandActualDateFormatted: formatted
                  }));
                }
              }}
              className={`w-full px-4 py-3 border-2 ${formData.polandActualDate ? 'border-green-400' : 'border-gray-300'} rounded-lg focus:border-gray-500 focus:outline-none transition-all font-mono text-lg`}
            />
          </div>

          {/* Fixing I Files Upload */}
          <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-all">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="w-1 h-8 bg-gray-600 mr-3"></div>
              Fixing I Result Files
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileInput
                label="Fixing I 15 min"
                system="fixingI"
                fileType="file15min"
                required={true}
                accept=".xlsx"
              />
              <FileInput
                label="Fixing I 60 min"
                system="fixingI"
                fileType="file60min"
                required={true}
                accept=".xlsx"
              />
            </div>
            <p className="mt-4 text-sm text-gray-600">
              15 min filename must start with <span className="font-mono">wynik_publiczny_{formData.polandActualDate.replace(/-/g, '')}_LOCAL_15</span>, 60 min with <span className="font-mono">wynik_publiczny_{formData.polandActualDate.replace(/-/g, '')}_LOCAL_60</span>
            </p>
          </div>

          {/* Update Button for Fixing I */}
          <button
            onClick={handleFixingIFilesSubmit}
            disabled={isSubmitting || !formData.polandActualDate || !formData.files.fixingI.file15min || !formData.files.fixingI.file60min}
            className={`w-full bg-gray-800 text-white py-4 px-6 rounded-xl font-semibold text-lg transform transition-all flex items-center justify-center space-x-3 ${
              formData.polandActualDate && formData.files.fixingI.file15min && formData.files.fixingI.file60min
                ? 'hover:bg-gray-900 hover:scale-[1.02]'
                : 'opacity-50 cursor-not-allowed'
            }`}
          >
            <Send className="w-6 h-6" />
            <span>{isSubmitting ? 'Uploading...' : 'Submit Fixing I Files'}</span>
          </button>
        </>
      )}

      {/* Fixing II Prices Tab Content */}
      {activeTab === 'fixingii' && (
        <>
          {/* Date Selection */}
          <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
            <label className="flex items-center space-x-3 text-lg font-semibold text-gray-700 mb-3">
              <Calendar className="w-6 h-6" />
              <span>Fixing II Prices for:</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.fixingIIDate}
              max={maxDateISO}
              onChange={(e) => {
                const newDate = e.target.value;
                if (newDate) {
                  const [year, month, day] = newDate.split('-');
                  const formatted = `${day}/${month}/${year}`;
                  setFormData(prev => ({ 
                    ...prev, 
                    fixingIIDate: newDate,
                    fixingIIDateFormatted: formatted
                  }));
                }
              }}
              className={`w-full px-4 py-3 border-2 ${formData.fixingIIDate ? 'border-green-400' : 'border-gray-300'} rounded-lg focus:border-gray-500 focus:outline-none transition-all font-mono text-lg`}
            />
          </div>

          {/* Fixing II Prices Input */}
          <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-all">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="w-1 h-8 bg-gray-600 mr-3"></div>
              Fixing II Quarterly Prices (PLN/MWh)
            </h2>

            <div className="relative border border-gray-300 rounded-md overflow-hidden h-[600px]">
              {/* Scrollable container for both labels and textarea */}
              <div className="flex h-full overflow-y-auto">
                {/* Quarter-hour labels */}
                <div className="w-32 bg-gray-50 border-r border-gray-300 flex-shrink-0" style={{ minHeight: `${fixingIISlots.length * 24 + 10}px` }}>
                  <div className="pt-2 pb-2">
                    {fixingIISlots.map((label, quarter) => (
                      <div key={quarter} className="h-6 flex items-center px-3">
                        <span className="text-xs font-mono text-gray-600 whitespace-nowrap">
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Textarea for prices */}
                <textarea
                  placeholder={`Paste ${fixingIISlots.length} quarterly prices here, one per line (every 15 minutes)`}
                  value={formData.fixingIIPricesText}
                  onChange={(e) => setFormData(prev => ({ ...prev, fixingIIPricesText: e.target.value }))}
                  className="flex-1 px-3 py-2 font-mono text-sm resize-none border-0 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  style={{
                    lineHeight: '24px',
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                    minHeight: `${fixingIISlots.length * 24}px` // lines * 24px line-height
                  }}
                />
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Enter {fixingIISlots.length} values, one for each 15-minute interval of the day
              {fixingIISlots.length !== 96 && ' (DST day)'}
            </p>
          </div>

          {/* Update Button for Fixing II */}
          <button
            onClick={handleFixingIISubmit}
            disabled={isSubmitting || !formData.fixingIIDate || !formData.fixingIIPricesText.trim()}
            className={`w-full bg-gray-800 text-white py-4 px-6 rounded-xl font-semibold text-lg transform transition-all flex items-center justify-center space-x-3 ${
              formData.fixingIIDate && formData.fixingIIPricesText.trim()
                ? 'hover:bg-gray-900 hover:scale-[1.02]'
                : 'opacity-50 cursor-not-allowed'
            }`}
          >
            <Send className="w-6 h-6" />
            <span>Update Fixing II Prices</span>
          </button>
        </>
      )}

      {/* Fixing II Orders Tab Content */}
      {activeTab === 'fixingIIOrders' && (
        <>
          {/* Date Selection */}
          <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
            <label className="flex items-center space-x-3 text-lg font-semibold text-gray-700 mb-3">
              <Calendar className="w-6 h-6" />
              <span>Fixing II Orders for:</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.fixingIIOrdersDate}
              max={maxDateISO}
              onChange={(e) => {
                const newDate = e.target.value;
                if (newDate) {
                  const [year, month, day] = newDate.split('-');
                  const formatted = `${day}/${month}/${year}`;
                  setFormData(prev => ({
                    ...prev,
                    fixingIIOrdersDate: newDate,
                    fixingIIOrdersDateFormatted: formatted
                  }));
                }
              }}
              className={`w-full px-4 py-3 border-2 ${formData.fixingIIOrdersDate ? 'border-green-400' : 'border-gray-300'} rounded-lg focus:border-gray-500 focus:outline-none transition-all font-mono text-lg`}
            />
          </div>

          {/* Fixing II Orders File Upload */}
          <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-all">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="w-1 h-8 bg-gray-600 mr-3"></div>
              Fixing II Orders File
            </h2>

            <div className="grid grid-cols-1 gap-4">
              <FileInput
                label="Fixing II Orders"
                system="fixingIIOrders"
                fileType="orders"
                required={true}
              />
            </div>
          </div>

          {/* Submit Button for Fixing II Orders */}
          <button
            onClick={handleFixingIIOrdersSubmit}
            disabled={isSubmitting || !formData.files.fixingIIOrders.orders || !formData.fixingIIOrdersDate}
            className={`w-full bg-gray-800 text-white py-4 px-6 rounded-xl font-semibold text-lg transform transition-all flex items-center justify-center space-x-3 ${
              formData.files.fixingIIOrders.orders && formData.fixingIIOrdersDate
                ? 'hover:bg-gray-900 hover:scale-[1.02]'
                : 'opacity-50 cursor-not-allowed'
            }`}
          >
            <Send className="w-6 h-6" />
            <span>{isSubmitting ? 'Uploading...' : 'Submit Fixing II Orders'}</span>
          </button>
        </>
      )}

      {/* Curves Tab Content */}
      {activeTab === 'curves' && (
        <>
          {/* Date Selection */}
          <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
            <label className="flex items-center space-x-3 text-lg font-semibold text-gray-700 mb-3">
              <Calendar className="w-6 h-6" />
              <span>Curves for:</span>
              <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.curvesDate}
              max={maxDateISO}
              onChange={(e) => {
                const newDate = e.target.value;
                if (newDate) {
                  const [year, month, day] = newDate.split('-');
                  const formatted = `${day}/${month}/${year}`;
                  setFormData(prev => ({
                    ...prev,
                    curvesDate: newDate,
                    curvesDateFormatted: formatted
                  }));
                }
              }}
              className={`w-full px-4 py-3 border-2 ${formData.curvesDate ? 'border-green-400' : 'border-gray-300'} rounded-lg focus:border-gray-500 focus:outline-none transition-all font-mono text-lg`}
            />
          </div>

          {/* Curves File Upload */}
          <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-all">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="w-1 h-8 bg-gray-600 mr-3"></div>
              Curves Files
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileInput
                label="Fixing I Curves (.xlsx / .xml)"
                system="curves"
                fileType="fixingICurves"
                required={false}
                accept=".xlsx,.xml"
              />
              <FileInput
                label="Fixing II Curves (.xml)"
                system="curves"
                fileType="fixingIICurves"
                required={false}
                accept=".xml"
              />
            </div>
            <p className="mt-4 text-sm text-gray-600">
              Upload at least one file. Fixing I Curves are .xlsx (hourly, up to 2026-06-10) or .xml (15-min, from 2026-06-11), Fixing II Curves must be .xml format.
            </p>
          </div>

          {/* Submit Button for Curves */}
          <button
            onClick={handleCurvesSubmit}
            disabled={isSubmitting || (!formData.files.curves.fixingICurves && !formData.files.curves.fixingIICurves) || !formData.curvesDate}
            className={`w-full bg-gray-800 text-white py-4 px-6 rounded-xl font-semibold text-lg transform transition-all flex items-center justify-center space-x-3 ${
              (formData.files.curves.fixingICurves || formData.files.curves.fixingIICurves) && formData.curvesDate
                ? 'hover:bg-gray-900 hover:scale-[1.02]'
                : 'opacity-50 cursor-not-allowed'
            }`}
          >
            <Send className="w-6 h-6" />
            <span>{isSubmitting ? 'Uploading...' : 'Submit Curves'}</span>
          </button>
        </>
      )}

      {/* Status message */}
      {status.message && (
        <div className={`p-4 rounded-lg flex items-center space-x-3 ${
          status.type === 'success' ? 'bg-green-100 text-green-800' :
          status.type === 'error' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {status.type === 'success' && <CheckCircle className="w-5 h-5" />}
          {status.type === 'error' && <XCircle className="w-5 h-5" />}
          {status.type === 'info' && <RefreshCw className="w-5 h-5 animate-spin" />}
          <p className="font-medium">{status.message}</p>
        </div>
      )}
    </div>
  );
};

export default DataUpload;
